import { useCallback, useState } from "react";

import type { InputMaybe, LangCode } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import Send from "@/components/ui/icon/send";

import type { ComponentProps } from "react";

type Props = {
	/** プレビューボタン(「テスト送信」)を表示しないかどうか */
	hidePreviewButton?: boolean;
	/** ラジオボタン表示時のオプション */
	radioOptions?: Array<{
		label: string;
		value: boolean;
		isSelected: boolean;
	}>;
	/** ラジオボタン変更時のコールバック */
	onRadioChange?: (value: boolean) => void;
	label: string;
	dialogTitle: string;
	value: InputMaybe<string> | undefined;
	errorMessage: string | undefined;
	handleChange: (input: string) => void;
	sendTestEmail: ({ lang, email }: { lang: LangCode; email: string }) => Promise<void>;
	lang: LangCode;
	hotelEmailAddress: string;
};

export const MailTemplateItem = ({
	hidePreviewButton = false,
	radioOptions,
	onRadioChange,
	label,
	value,
	errorMessage,
	handleChange,
	sendTestEmail,
	dialogTitle,
	lang,
	hotelEmailAddress,
}: Props) => {
	const [emailAddress, setEmailAddress] = useState<string>(hotelEmailAddress);
	const handleEmailAddressChange = useCallback((input: string) => {
		setEmailAddress(input);
	}, []);

	const [open, setOpen] = useState(false);

	const handleSendTestEmail = useCallback(async () => {
		await sendTestEmail({
			lang,
			email: emailAddress,
		});
	}, [emailAddress, lang, sendTestEmail]);

	const buttonVariant: ComponentProps<typeof Button>["variant"] = emailAddress ? "default" : "disabled";

	const handleClickPreviewButton = useCallback(() => {
		setOpen(true);
	}, []);

	// ラジオボタンで「送信する」が選択されているかどうか、またはラジオボタンが存在しない場合は常に表示
	const isEnabled = radioOptions ? radioOptions.some((option) => option.value === true && option.isSelected) : true;

	return (
		<div className="mt-5">
			{radioOptions && <p className="mb-3 text-sm font-semibold">{label}</p>}
			<div className="space-y-3">
				<div className="flex justify-between">
					{!radioOptions && <p className="mb-3 text-sm font-semibold">{label}</p>}
					{radioOptions && (
						<div className="space-y-2">
							{radioOptions.map((option) => (
								<label
									key={`option-${label}-${option.label}`}
									className="flex cursor-pointer items-center space-x-2"
								>
									<input
										type="radio"
										name={`${label}-radio`}
										value={option.value.toString()}
										checked={option.isSelected}
										onChange={() => onRadioChange?.(option.value)}
										className="border-gray-300 h-4 w-4 text-black accent-black focus:ring-black"
									/>
									<span className="text-sm">{option.label}</span>
								</label>
							))}
						</div>
					)}
					{!hidePreviewButton && isEnabled && (
						<Button
							variant={"outline"}
							type={"button"}
							onClick={handleClickPreviewButton}
							icon={<Send size="sm" />}
						>
							テスト送信
						</Button>
					)}
				</div>

				{isEnabled && (
					<TextAreaField
						value={value ?? undefined}
						handleChange={handleChange}
						errorMessage={errorMessage}
						placeholder="オリジナルの文面を追加したい場合はこちらに記入をしてください。送られる文面は右上の「テスト送信」から確認できます。"
					/>
				)}
			</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="space-y-4">
					<DialogHeader>
						<DialogTitle>{dialogTitle}に送られるメールのテスト送信</DialogTitle>
						<DialogDescription className="text-xs">
							{/* テキストボックスに入力されているメールテンプレートの内容が上記のアドレス宛に送信されます。 */}
							{/* 確認後は必ず、右上の「保存」もしくは「更新」ボタンを押して、内容を確定させてください。*/}
							文言を追加していない場合も、デフォルトのメール内容を確認いただけます
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-8">
						<InputTextField
							label="送信先メールアドレス"
							value={emailAddress}
							handleChange={handleEmailAddressChange}
							errorMessage={undefined}
						/>
						<div className="flex justify-end">
							<Button type="button" size="lg" variant={buttonVariant} onClick={handleSendTestEmail}>
								送信
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

MailTemplateItem.displayName = "MailTemplateItem";
